import { useEffect, type ReactNode } from "react";
import { navigate, useLocationUrl } from "../routing/useLocationPath";
import { useSession } from "./session";
import { authRoute, authUrl, defaultAuthDestination, destinationFromSearch, safeAuthDestination } from "./destination";
import { AuthLoading, AuthShell } from "./AuthShell";
import { LoginPage } from "./LoginPage";
import { SignupPage } from "./SignupPage";

const destinationStorageKey = "signalbill:auth-destination";
function retainedDestination(): string | null {
  try { return window.sessionStorage.getItem(destinationStorageKey); } catch { return null; }
}

function AuthRedirect({ to }: { to: string }) {
  useEffect(() => { navigate(to, { replace: true }); }, [to]);
  return <AuthShell><AuthLoading /></AuthShell>;
}

// Signed-in children only mount after AccountStatusProvider reaches workspace-ready.
export function AuthRoutes({ children }: { children: ReactNode }) {
  const { state } = useSession();
  const location = useLocationUrl();
  const url = new URL(location, "https://app.signalbill.com");
  const route = authRoute(url.pathname);
  const nested = Boolean(route && url.pathname !== `/${route}`);
  const hasDestination = url.searchParams.has("redirect");
  // Clerk can reload a nested factor URL without the entry query. Retain only
  // validated navigation state in this tab; explicit entries reset the default.
  const destination = nested && !hasDestination
    ? safeAuthDestination(retainedDestination()) : destinationFromSearch(url.search);
  useEffect(() => {
    try {
      if (route) window.sessionStorage.setItem(destinationStorageKey, destination);
      else window.sessionStorage.removeItem(destinationStorageKey);
    } catch { /* Storage unavailable: keep the validated URL/default behavior. */ }
    if (nested && !hasDestination && destination !== defaultAuthDestination) {
      const restored = new URL(location, "https://app.signalbill.com");
      restored.searchParams.set("redirect", destination);
      navigate(`${restored.pathname}${restored.search}${restored.hash}`, { replace: true });
    }
  }, [destination, hasDestination, location, nested, route]);
  if (state === "config-missing") return <AuthShell title="Sign in to SignalBill"><p className="auth-status" role="status">Authentication is not configured. Please try again later.</p></AuthShell>;
  if (state === "loading") return <AuthShell><AuthLoading title={route === "signup" ? "Create your SignalBill account" : route === "login" ? "Sign in to SignalBill" : undefined} /></AuthShell>;
  if (state === "signed-out") {
    if (route === "login") return <LoginPage destination={destination} />;
    if (route === "signup") return <SignupPage destination={destination} />;
    return <AuthRedirect to={authUrl("login", location)} />;
  }
  if (route) return <AuthRedirect to={destination} />;
  return children;
}
